import type { ConnectorType, EmailSecurityMode, NotificationDeliveryStatus } from '@qms360/shared';
import { apiFetch } from './api.js';

/** The Integration Cockpit (spec §13.2) and the email outbox. */

export interface ConnectorView {
  type: ConnectorType;
  label: string;
  description: string;
  availability: 'available' | 'extension_point' | 'stub' | 'later';
  unavailableReason: string | null;
  secretLabel: string | null;
  connector: {
    isEnabled: boolean;
    isSandbox: boolean;
    config: Record<string, unknown>;
    health: 'connected' | 'degraded' | 'failed' | 'disabled';
    lastHealthCheckAt: string | null;
    lastHealthMessage: string | null;
    updatedAt: string;
  } | null;
  secret: { set: boolean; hint: string | null; setAt: string | null; unreadable: boolean } | null;
}

export interface IntegrationsView {
  connectors: ConnectorView[];
  canChange: boolean;
  vaultAvailable: boolean;
  builtIn: { key: string; label: string; description: string; settingsPath: string | null }[];
}

export interface ProjectSyncConfigInput {
  source: 'sample' | 'url';
  url: string | null;
  recordsPath: string | null;
  externalSource: string;
  scheduleIntervalMinutes: number | null;
}

export interface EmailConfigInput {
  host: string;
  port: number;
  security: EmailSecurityMode;
  username: string | null;
  fromAddress: string;
  fromName: string | null;
  sandboxRecipient: string | null;
}

export const fetchIntegrations = () => apiFetch<IntegrationsView>('/admin/integrations');

export const updateConnector = (
  type: ConnectorType,
  /** The config is checked against the connector type's own schema by the server. */
  body: { config?: EmailConfigInput | { subject: string } | ProjectSyncConfigInput; isEnabled?: boolean; isSandbox?: boolean },
) =>
  apiFetch<{ connector: ConnectorView }>(`/admin/integrations/${type}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

export const storeConnectorSecret = (type: ConnectorType, secret: string) =>
  apiFetch<{ credential: { set: boolean; hint: string | null } }>(`/admin/integrations/${type}/secret`, {
    method: 'PUT',
    body: JSON.stringify({ secret }),
  });

export const removeConnectorSecret = (type: ConnectorType) =>
  apiFetch<void>(`/admin/integrations/${type}/secret`, { method: 'DELETE' });

export interface TestResult {
  health: 'connected' | 'failed' | 'disabled';
  message: string;
  testMessage: { sent: boolean; to: string | null; message: string } | null;
}

export const testConnector = (type: ConnectorType, sendTestMessage: boolean) =>
  apiFetch<TestResult>(`/admin/integrations/${type}/test`, {
    method: 'POST',
    body: JSON.stringify({ sendTestMessage }),
  });

export interface OutboxMessage {
  id: string;
  app: string | null;
  templateKey: string | null;
  subject: string;
  recipientName: string;
  recipientEmail: string;
  deliveredTo: string | null;
  deliveryStatus: NotificationDeliveryStatus;
  failureReason: string | null;
  attempts: number;
  isCritical: boolean;
  createdAt: string;
  sentAt: string | null;
}

export interface OutboxPage {
  messages: OutboxMessage[];
  nextCursor: string | null;
  counts: { pending: number; notSent: number; failed: number };
  emailReady: boolean;
  notReadyReason: string | null;
  canRequeue: boolean;
}

export const fetchOutbox = (params: { status?: NotificationDeliveryStatus | ''; before?: string | null } = {}) => {
  const query = new URLSearchParams();
  if (params.status) query.set('status', params.status);
  if (params.before) query.set('before', params.before);
  const suffix = query.toString();
  return apiFetch<OutboxPage>(`/admin/email-outbox${suffix ? `?${suffix}` : ''}`);
};

export const requeueMessage = (id: string) =>
  apiFetch<{ deliveryStatus: 'pending' }>(`/admin/email-outbox/${id}/requeue`, { method: 'POST' });

/** New push signing keys. Every device subscribed under the old ones is retired. */
export const generatePushKeys = () =>
  apiFetch<{ connector: ConnectorView }>('/admin/integrations/push/keys', { method: 'POST' });
